import { useEffect, useRef, useState } from "react";

const PREVIEW_DEBOUNCE_MS = 1000;

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

export default function ContentLibraryPdfPreview({ src = null, previewUrl = null, body = "", versionNumber = null, recordId = null, versionId = null, title = "PDF preview", className = "", enabled = true, hideNativeToolbar = false }) {
  const [pdfSource, setPdfSource] = useState(src);
  const [isLoading, setIsLoading] = useState(Boolean(src || previewUrl));
  const [error, setError] = useState("");
  const objectUrlRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !previewUrl) {
      setPdfSource(src);
      setIsLoading(Boolean(src));
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setError("");
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(previewUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken()
          },
          body: JSON.stringify({
            body,
            version_number: versionNumber,
            record_id: recordId,
            version_id: versionId
          }),
          signal: controller.signal
        });

        if (!response.ok) throw new Error("PDF preview failed");

        const blob = await response.blob();
        const nextObjectUrl = URL.createObjectURL(blob);

        if (requestId !== requestIdRef.current) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = nextObjectUrl;
        setPdfSource(nextObjectUrl);
      } catch (requestError) {
        if (requestError.name === "AbortError") return;

        setPdfSource(null);
        setIsLoading(false);
        setError("PDF-previewet kunne ikke genereres.");
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [body, enabled, previewUrl, recordId, src, versionId, versionNumber]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  return (
    <div className={`content-library-show-pdf ${hideNativeToolbar ? "content-library-show-pdf--minimal" : ""} ${className}`.trim()}>
      {pdfSource ? (
        <iframe
          className="content-library-show-pdf-frame"
          src={pdfSource}
          title={title}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setError("PDF-previewet kunne ikke indlæses.");
          }}
        />
      ) : null}

      {isLoading ? (
        <div className="content-library-show-pdf__status" role="status" aria-live="polite">
          <span className="spinner-border text-secondary" aria-hidden="true" />
          <span>{previewUrl ? "PDF genereres…" : "PDF indlæses…"}</span>
        </div>
      ) : null}

      {error ? (
        <div className="content-library-show-pdf__status content-library-show-pdf__status--error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
